import { z } from 'zod';

import { DailyEnergyQuotaDto } from './energy-quotas';
import { PlayerStatusDto } from './user-context';

export const MISSION_STATUSES = ['ASSIGNED', 'COMPLETED', 'SKIPPED', 'FAILED'] as const;
export const MissionStatus = z.enum(MISSION_STATUSES);
export type MissionStatus = z.infer<typeof MissionStatus>;

export const MISSION_SCORE_STATUSES = ['PENDING', 'SCORED'] as const;
export const MissionScoreStatus = z.enum(MISSION_SCORE_STATUSES);
export type MissionScoreStatus = z.infer<typeof MissionScoreStatus>;

/** Дневной потолок XP осознанности за один отчёт (шкала LLM-скоринга 0–50).
 *  Единый источник: бэкенд-кламп (key-state) и фронт-бейдж «до N XP» берут отсюда,
 *  чтобы число в UI не разъехалось с реальным капом. */
export const XP_DAILY_CAP = 50;

export const GameProfileDto = z.object({
  awarenessXp: z.number().int(),
  statusLevel: z.number().int(),
  status: PlayerStatusDto,
  nextStatus: PlayerStatusDto.nullable(),
  progressToNext: z.number().int().min(0).max(100),
  streak: z.number().int(),
  decayActive: z.boolean(),
  /** % XP, сгорающего за ночь простоя (GAME_DECAY_PERCENT) — чтобы UI показывал
   *  реальное число, а не хардкод. */
  decayPercent: z.number().int().min(0).max(100),
  /** Квота бесплатных игровых действий (отчёт / смена миссии) за сутки. */
  requestQuota: DailyEnergyQuotaDto,
});
export type GameProfileDto = z.infer<typeof GameProfileDto>;

/** Один затронутый скорингом ключ — для экрана результата (уровень до→после + инсайт). */
export const KeyOutcomeEntryDto = z.object({
  number: z.number().int(),
  name: z.string(),
  nameEn: z.string().optional(),
  levelBefore: z.number().int(),
  levelAfter: z.number().int(),
  /** Инсайт пользователя по ключу (может быть пустым). */
  insight: z.string(),
});
export type KeyOutcomeEntryDto = z.infer<typeof KeyOutcomeEntryDto>;

/** Разбор отчёта: какие ключи выросли + фокус на завтра. Показывается после SCORED. */
export const KeyOutcomeDto = z.object({
  keys: z.array(KeyOutcomeEntryDto),
  /** Задача-фокус на следующие 24ч из разбора (первый непустой focus среди ключей); null, если не предложен. */
  focusText: z.string().nullable(),
});
export type KeyOutcomeDto = z.infer<typeof KeyOutcomeDto>;

export const DailyMissionDto = z.object({
  id: z.string(),
  sphereSlug: z.string(),
  title: z.string(),
  titleEn: z.string().optional(),
  description: z.string(),
  descriptionEn: z.string().optional(),
  xpReward: z.number().int(),
  status: MissionStatus,
  assignedDate: z.string(),
  /** Снапшот «фокуса дня» (задача на 24ч из вчерашнего разбора ключей); null, если фокуса не было. */
  focusText: z.string().nullable(),
  /** Название ключа, из которого взят фокус, — для подписи в UI. */
  focusKeyName: z.string().nullable(),
  focusKeyNameEn: z.string().optional(),
  report: z
    .object({
      done: z.boolean(),
      note: z.string().nullable(),
      scoreStatus: MissionScoreStatus,
      xpAwarded: z.number().int(),
      /** Разбор ключей; null пока PENDING, для не-done и для отчётов до релиза фичи. */
      outcome: KeyOutcomeDto.nullable(),
    })
    .nullable(),
  /** Прогресс стартового трека; null, если миссия дня не стартовая. */
  onboarding: z.object({ completed: z.number().int(), total: z.number().int() }).nullable(),
});
export type DailyMissionDto = z.infer<typeof DailyMissionDto>;

export const MissionTemplateDto = z.object({
  slug: z.string(),
  sphereSlug: z.string(),
  title: z.string(),
  titleEn: z.string().optional(),
  description: z.string(),
  descriptionEn: z.string().optional(),
  xpReward: z.number().int(),
  isOnboarding: z.boolean(),
});
export type MissionTemplateDto = z.infer<typeof MissionTemplateDto>;

export const SwitchMissionInput = z.object({ missionTemplateSlug: z.string().min(1) });
export type SwitchMissionInput = z.infer<typeof SwitchMissionInput>;

export const VECTOR_TIERS = ['START', 'ARCHITECT'] as const;
export const VectorTier = z.enum(VECTOR_TIERS);
export type VectorTier = z.infer<typeof VectorTier>;

export const VECTOR_ITEM_TEXT_MAX = 500;

/** Сколько строк вектора читает ИИ: быстрый подбор vs глубокий разбор.
 *  Это НЕ лимит хранения — вектор в БД не режется. */
export const VECTOR_AI_QUICK = 3;
export const VECTOR_AI_DEEP = 12;

/** Потолок одной страницы выдачи API на вектор. */
export const VECTOR_ENTRIES_PAGE_DEFAULT = 50;
export const VECTOR_ENTRIES_PAGE_MAX = 500;

export const SPHERE_RATING_MIN = 1;
export const SPHERE_RATING_MAX = 10;

/** Оценки сфер жизни для вектора 11 «Баланс сфер»: slug активной LifeSphere → 1..10.
 *  Здесь проверяется только ФОРМА. Полноту набора и принадлежность slug'ов активному
 *  каталогу проверяет сервис по БД (`LifeSphere.isActive`) — каталог не дублируем. */
export const SphereRatingsDto = z.record(
  z.string().min(1),
  z.number().int().min(SPHERE_RATING_MIN).max(SPHERE_RATING_MAX),
);
export type SphereRatingsDto = z.infer<typeof SphereRatingsDto>;

/** Строка вектора: текст, приоритет (1 = верх) и привязанные люди (0..N). */
export const VectorItemDto = z.object({
  id: z.uuid(),
  text: z.string().max(VECTOR_ITEM_TEXT_MAX),
  position: z.number().int().min(1),
  /** Порядок значим: он же порядок аватарок в UI. */
  personIds: z.array(z.uuid()),
  /** Только вектор 11; для остальных всегда null. */
  sphereRatings: SphereRatingsDto.nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type VectorItemDto = z.infer<typeof VectorItemDto>;

export const CreateVectorItemInput = z.object({
  text: z.string().trim().min(1).max(VECTOR_ITEM_TEXT_MAX),
  personIds: z.array(z.uuid()).optional(),
  /** Обязателен для вектора 11 и запрещён для остальных — номер вектора приходит
   *  path-параметром, поэтому связку проверяет сервис, а не схема. */
  sphereRatings: SphereRatingsDto.optional(),
});
export type CreateVectorItemInput = z.infer<typeof CreateVectorItemInput>;

export const UpdateVectorItemInput = z
  .object({
    text: z.string().trim().min(1).max(VECTOR_ITEM_TEXT_MAX).optional(),
    /** Полный набор привязок (replace). `[]` — отвязать всех. */
    personIds: z.array(z.uuid()).optional(),
    /** Только вектор 11: переписывает снимок целиком (replace). */
    sphereRatings: SphereRatingsDto.optional(),
  })
  .refine((v) => v.text !== undefined || v.personIds !== undefined || v.sphereRatings !== undefined, {
    message: 'nothing to update',
  });
export type UpdateVectorItemInput = z.infer<typeof UpdateVectorItemInput>;

/** Полный порядок вектора: itemIds обязан покрывать ровно его строки, лимита нет. */
export const ReorderVectorItemsInput = z.object({ itemIds: z.array(z.uuid()).min(1) });
export type ReorderVectorItemsInput = z.infer<typeof ReorderVectorItemsInput>;

/** Один из 12 векторов осознанности в карте пользователя (read-only, ИИ-only). */
export const VectorStateDto = z.object({
  number: z.number().int().min(1).max(12),
  name: z.string(),
  description: z.string(),
  tier: VectorTier,
  /** Тариф-заглушка: true для ARCHITECT-векторов (4–12) — UI показывает locked. */
  locked: z.boolean(),
  /** Ранж-массив пунктов (важное сверху). У колеса-11 это комментарии к снимкам. */
  items: z.array(z.string()),
  /** Полные строки вектора. `items` остаётся производным массивом текстов —
   *  его читает mini-app, ломать контракт нельзя. */
  entries: z.array(VectorItemDto),
  /** Вектор 11 «Баланс сфер» — рисуется колесом, не списком. */
  isWheel: z.boolean(),
});
export type VectorStateDto = z.infer<typeof VectorStateDto>;

export const VectorOnboardingEntryKind = z.enum(['list', 'wheel', 'people']);
export type VectorOnboardingEntryKind = z.infer<typeof VectorOnboardingEntryKind>;

/** Только серверные данные: тексты вопроса, minItems и entryKind клиент берёт
 *  из каталога `@upme/view-logic` (API не владеет UI-копирайтом и i18n). */
export const VectorOnboardingPendingVectorDto = z.object({
  number: z.number().int().min(1).max(12),
  itemsCollected: z.number().int().min(0),
});
export type VectorOnboardingPendingVectorDto = z.infer<typeof VectorOnboardingPendingVectorDto>;

export const VectorOnboardingStateDto = z.object({
  status: z.enum(['active', 'complete']),
  gameDate: z.string(),
  answersToday: z.number().int().min(0),
  missionUnlocked: z.boolean(),
  pendingVectors: z.array(VectorOnboardingPendingVectorDto),
  completedVectors: z.array(z.number().int().min(1).max(12)),
  completedAt: z.iso.datetime().nullable(),
});
export type VectorOnboardingStateDto = z.infer<typeof VectorOnboardingStateDto>;

export const SubmitReportInput = z
  .object({
    done: z.boolean(),
    note: z.string().trim().max(1000).optional(),
  })
  .refine((d) => !d.done || (d.note != null && d.note.length >= 10), {
    message: 'note required (min 10 chars) when done',
    path: ['note'],
  });
export type SubmitReportInput = z.infer<typeof SubmitReportInput>;

type PlayerStatusLike = {
  level: number;
  slug: string;
  name: string;
  threshold: number;
  description: string;
};

export interface ResolvedStatus {
  statusLevel: number;
  status: PlayerStatusDto;
  nextStatus: PlayerStatusDto | null;
  progressToNext: number;
}

/**
 * Текущий статус по накопленному XP. statuses сортируются по threshold возр.
 * progressToNext — % пути от порога текущего статуса до следующего; на максимуме
 * nextStatus=null, прогресс=100.
 */
export function resolveStatus(xp: number, statuses: PlayerStatusLike[]): ResolvedStatus {
  if (statuses.length === 0) throw new Error('resolveStatus: statuses must be non-empty');
  const sorted = [...statuses].sort((a, b) => a.threshold - b.threshold);
  let current = sorted[0]!;
  let next: PlayerStatusLike | null = null;
  for (let i = 0; i < sorted.length; i += 1) {
    if (xp >= sorted[i]!.threshold) {
      current = sorted[i]!;
      next = sorted[i + 1] ?? null;
    }
  }
  // span может быть 0, если два статуса делят один threshold (дубликат в БД) —
  // тогда деление дало бы NaN и сломало бы валидацию GameProfileDto. Считаем
  // вырожденный интервал как уже пройденный (100%).
  const span = next ? next.threshold - current.threshold : 0;
  const progressToNext =
    next && span > 0 ? Math.min(100, Math.max(0, Math.round(((xp - current.threshold) / span) * 100))) : 100;
  return { statusLevel: current.level, status: current, nextStatus: next, progressToNext };
}
